import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { ListChecks, Download, AlertTriangle } from 'lucide-react';

const INTENT_STYLE = {
  heating: 'text-white bg-white/10 border-white/20',
  cooling: 'text-lime bg-lime/10 border-lime/30',
  both: 'text-light-gray bg-black border-divider',
  neutral: 'text-gray bg-black border-divider',
};

export default function AdminCampaignReference() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [trade, setTrade] = useState('All');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [filter, setFilter] = useState('');
  const [showMapping, setShowMapping] = useState(false);

  useEffect(() => {
    api.get('/admin/campaign-reference')
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load reference'));
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const f = filter.trim().toLowerCase();
    return data.rows.filter((r) =>
      (trade === 'All' || r.trade === trade) &&
      (!flaggedOnly || r.flags.length > 0) &&
      (!f || r.campaign_name.toLowerCase().includes(f) || r.symptom_tags.some((t) => t.toLowerCase().includes(f)))
    );
  }, [data, trade, flaggedOnly, filter]);

  const downloadCsv = async () => {
    const res = await api.get('/admin/campaign-reference/csv', { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gritclicks_campaign_reference.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout variant="light">
      <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-campaign-reference-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <h1 className="text-4xl font-black text-white flex items-center gap-3"><ListChecks className="text-lime" size={32} /> Campaign Reference</h1>
          <button onClick={downloadCsv} data-testid="reference-download-csv-btn"
            className="flex items-center gap-2 bg-lime text-black font-bold px-4 py-2 rounded-lg hover:opacity-90 transition-opacity">
            <Download size={16} /> Download CSV
          </button>
        </div>
        <p className="text-light-gray mb-6">Every campaign with its trade, seasons, symptom tags, goal and price range — plus the keyword mapping that powers search. Flagged rows may have mismatched tags.</p>
        {error && <p className="text-orange" data-testid="reference-error">{error}</p>}
        {data && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="text-sm text-gray" data-testid="reference-counts">{data.total} campaigns · <span className="text-orange">{data.flagged} flagged</span></span>
              {['All', 'HVAC', 'Plumbing', 'Electrical'].map((t) => (
                <button key={t} onClick={() => setTrade(t)} data-testid={`reference-trade-${t.toLowerCase()}`}
                  className={`px-4 py-1.5 rounded-full border text-sm font-bold transition-colors ${trade === t ? 'bg-lime text-black border-lime' : 'border-divider text-light-gray hover:border-lime/50'}`}>
                  {t}
                </button>
              ))}
              <button onClick={() => setFlaggedOnly(!flaggedOnly)} data-testid="reference-flagged-toggle"
                className={`px-4 py-1.5 rounded-full border text-sm font-bold transition-colors ${flaggedOnly ? 'bg-orange text-black border-orange' : 'border-divider text-light-gray hover:border-orange/60'}`}>
                Flagged only
              </button>
              <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name or tag…"
                data-testid="reference-filter-input"
                className="bg-card-bg border border-divider rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray focus:border-lime focus:outline-none" />
              <button onClick={() => setShowMapping(!showMapping)} data-testid="reference-mapping-toggle"
                className="text-sm text-lime underline underline-offset-2">
                {showMapping ? 'Hide' : 'Show'} search keyword mapping
              </button>
            </div>

            {showMapping && (
              <div className="card mb-6 p-5" data-testid="reference-mapping-panel">
                <p className="text-sm text-light-gray mb-4">{data.keyword_mapping.notes}</p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white font-bold mb-1">Heating search terms → heating campaigns only</p>
                    <p className="text-light-gray mb-3">{data.keyword_mapping.heating_query_terms.join(', ')}</p>
                    <p className="text-white font-bold mb-1">Cooling search terms → cooling campaigns only</p>
                    <p className="text-light-gray">{data.keyword_mapping.cooling_query_terms.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-white font-bold mb-1">Synonym expansion (60% weight)</p>
                    <div className="text-light-gray max-h-48 overflow-y-auto pr-2">
                      {Object.entries(data.keyword_mapping.synonyms).map(([k, v]) => (
                        <p key={k}><span className="text-lime">{k}</span> → {v.join(', ')}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="card overflow-x-auto" data-testid="reference-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray border-b border-divider">
                    <th className="pb-3 pr-4">Trade</th>
                    <th className="pb-3 pr-4">Campaign</th>
                    <th className="pb-3 pr-4">Season(s)</th>
                    <th className="pb-3 pr-4">Symptom Tags</th>
                    <th className="pb-3 pr-4">Goal</th>
                    <th className="pb-3 pr-4">Price Range</th>
                    <th className="pb-3 pr-4">Intent</th>
                    <th className="pb-3">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={`border-b border-divider/50 align-top ${r.flags.length ? 'bg-orange/5' : ''}`} data-testid={`reference-row-${r.id}`}>
                      <td className="py-3 pr-4 text-light-gray whitespace-nowrap">{r.trade}</td>
                      <td className="py-3 pr-4 text-white font-bold">{r.campaign_name}</td>
                      <td className="py-3 pr-4 text-light-gray whitespace-nowrap">{r.seasons.join(', ')}</td>
                      <td className="py-3 pr-4 text-light-gray max-w-md">{r.symptom_tags.join(', ')}</td>
                      <td className="py-3 pr-4 text-light-gray whitespace-nowrap">{r.goal_tag}</td>
                      <td className="py-3 pr-4 text-light-gray whitespace-nowrap">{r.avg_ticket}</td>
                      <td className="py-3 pr-4">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${INTENT_STYLE[r.intent]}`}>{r.intent}</span>
                      </td>
                      <td className="py-3 max-w-sm">
                        {r.flags.map((f, i) => (
                          <p key={i} className="text-orange text-xs flex items-start gap-1 mb-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" /> {f}</p>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
